import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import { Warning } from '@mui/icons-material';

/**
 * ValidationDialog Component
 * 
 * Shows validation errors when user tries to change sections with incomplete required fields.
 * Prevents navigation until all required fields are completed.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Array} props.missingItems - Array of missing field descriptions
 * @param {number} props.currentSection - Current section number
 * @param {number} props.targetSection - Section user was trying to navigate to
 * @param {Object} props.surveyInfo - Survey structure information (optional)
 */
const ValidationDialog = ({
  open,
  onClose,
  missingItems = [],
  currentSection,
  targetSection,
  surveyInfo,
}) => {
  // Helper function to get section names
  const getSectionName = (sectionNumber) => {
    if (!surveyInfo) {
      return `Section ${sectionNumber}`;
    }
    
    const sectionKeys = Object.keys(surveyInfo);
    return sectionKeys[sectionNumber - 1] || `Section ${sectionNumber}`;
  };
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          height: '200vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Warning color="warning" sx={{ fontSize: 32 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Required Fields Missing
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          {getSectionName(currentSection)} has required fields that must be completed before you can continue to {getSectionName(targetSection)}.
        </Typography>
        
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Please complete the following:
        </Typography>
        
        <List sx={{ bgcolor: 'grey.50', borderRadius: 1, py: 1 }}>
          {missingItems.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Box 
                  sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: 'warning.main' 
                  }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary={item}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { fontWeight: 500 }
                }}
              />
            </ListItem>
          ))}
        </List>
        
        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
          <strong>Note:</strong> For questions answered "No", you must provide an explanation 
          (minimum 10 characters) and assign responsibility.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          size="large"
          sx={{ 
            minWidth: 120,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          OK, I'll Complete Them
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationDialog;